/**
 * AdminContactSubmissions — JCC Admin view for the public Contact form.
 *
 * Route protection: ProtectedRoute requireAdmin (App.js), same JWT admin
 * check as every other /admin/* page. Reads/writes the existing
 * contact_submissions collection — no data is deleted or restructured.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2, XCircle, HelpCircle, Circle, CheckCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import { Button } from "../../components/ui/button";

const AUDIENCE_LABEL = { carer: "Carer", family: "Family", other: "Other" };

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function EmailSentBadge({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="submission-email-sent-yes">
        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Notified
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700" data-testid="submission-email-sent-no">
        <XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Not delivered
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted" data-testid="submission-email-sent-unknown">
      <HelpCircle className="w-3.5 h-3.5" strokeWidth={2} /> Unknown
    </span>
  );
}

export default function AdminContactSubmissions() {
  const [submissions, setSubmissions] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unread
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/contact");
      setSubmissions(data.submissions || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load contact submissions");
      setSubmissions([]);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/api/admin/contact/${id}/status`, { status });
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update status");
    } finally {
      setBusyId(null);
    }
  };

  const unreadCount = (submissions || []).filter((s) => s.status !== "read").length;
  const visible = (submissions || []).filter((s) => (filter === "unread" ? s.status !== "read" : true));

  return (
    <div data-testid="admin-contact-submissions-page">
      <PageHero
        eyebrow="JCC Admin"
        title="Contact Submissions"
        description="Messages sent through the public Contact form."
      >
        <Link to="/admin" className="btn-outline text-sm inline-flex items-center gap-2" data-testid="admin-contact-back">
          <ArrowLeft className="w-4 h-4" /> Back to Admin home
        </Link>
      </PageHero>

      <section className="section pt-8">
        <div className="container-jcc max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setFilter("all")}
              className={`pill text-sm ${filter === "all" ? "bg-teal text-white" : "bg-slate-100 text-ink hover:bg-slate-200"}`}
              data-testid="admin-contact-filter-all"
            >
              All ({(submissions || []).length})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`pill text-sm ${filter === "unread" ? "bg-teal text-white" : "bg-slate-100 text-ink hover:bg-slate-200"}`}
              data-testid="admin-contact-filter-unread"
            >
              Unread ({unreadCount})
            </button>
          </div>

          {submissions === null && <p className="text-ink-muted">Loading…</p>}
          {submissions !== null && visible.length === 0 && (
            <p className="text-ink-muted" data-testid="admin-contact-empty">No submissions to show.</p>
          )}

          <div className="space-y-3">
            {visible.map((s) => {
              const isUnread = s.status !== "read";
              return (
                <div
                  key={s.id}
                  className={`card-base p-5 md:p-6 ${isUnread ? "border-teal/40 bg-teal/[0.03]" : ""}`}
                  data-testid={`contact-submission-${s.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isUnread && <Circle className="w-2.5 h-2.5 fill-teal text-teal" data-testid={`submission-unread-dot-${s.id}`} />}
                        <h3 className="font-display font-semibold text-ink" data-testid={`submission-subject-${s.id}`}>
                          {s.subject || `Message from ${s.name}`}
                        </h3>
                        <span className="pill text-xs bg-slate-100 text-ink-muted" data-testid={`submission-audience-${s.id}`}>
                          {AUDIENCE_LABEL[s.audience] || "Other"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-ink-muted" data-testid={`submission-meta-${s.id}`}>
                        {s.name} ·{" "}
                        <a href={`mailto:${s.email}`} className="text-teal hover:underline" data-testid={`submission-email-${s.id}`}>
                          {s.email}
                        </a>{" "}
                        · {fmtDateTime(s.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <EmailSentBadge value={s.email_sent} />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === s.id}
                        onClick={() => setStatus(s.id, isUnread ? "read" : "new")}
                        data-testid={`submission-toggle-status-${s.id}`}
                      >
                        <CheckCheck className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                        {isUnread ? "Mark read" : "Mark unread"}
                      </Button>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-ink leading-relaxed whitespace-pre-wrap" data-testid={`submission-message-${s.id}`}>
                    {s.message}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
